import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getPermissions, getRolePermissions, logActivity } from '../../../services/teamService';

const PermissionsTab = () => {
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState('admin');
  const [pendingChanges, setPendingChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const permsData = await getPermissions();
      setPermissions(permsData);

      // Load permissions for all roles
      const roles = ['admin', 'operator', 'viewer'];
      const rolePermsData = {};
      for (const role of roles) {
        const perms = await getRolePermissions(role);
        rolePermsData[role] = perms;
      }
      setRolePermissions(rolePermsData);
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full system access with all permissions', icon: 'ShieldCheck' },
    { value: 'operator', label: 'Operator', description: 'Manage customers, projects, contractors, and permits', icon: 'Shield' },
    { value: 'viewer', label: 'Viewer', description: 'View-only access to all resources', icon: 'Eye' }
  ];

  const resources = [
    { key: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { key: 'projects', label: 'Projects', icon: 'Briefcase' },
    { key: 'customers', label: 'Customers', icon: 'Users' },
    { key: 'contractors', label: 'Contractors', icon: 'HardHat' },
    { key: 'permits', label: 'Permits', icon: 'FileCheck' }
  ];

  const actions = ['view', 'edit', 'admin'];

  const hasPermission = (role, resource, action) => {
    const key = `${role}:${resource}:${action}`;
    if (key in pendingChanges) return pendingChanges?.[key];
    return rolePermissions?.[role]?.some(rp =>
      rp?.permissions?.resource === resource && rp?.permissions?.action === action
    );
  };

  const handleTogglePermission = (role, resource, action) => {
    const key = `${role}:${resource}:${action}`;
    const current = hasPermission(role, resource, action);
    setPendingChanges(prev => ({ ...prev, [key]: !current }));
  };

  const handleSavePermissions = async () => {
    if (Object.keys(pendingChanges)?.length === 0) return;
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const { supabase } = await import('../../../lib/supabase');
      for (const [key, shouldHave] of Object.entries(pendingChanges)) {
        const [role, resource, action] = key?.split(':');
        // Find the permission record
        const perm = permissions?.find(p => p?.resource === resource && p?.action === action);
        if (!perm) continue;

        const existing = rolePermissions?.[role]?.find(
          rp => rp?.permissions?.resource === resource && rp?.permissions?.action === action
        );

        if (shouldHave && !existing) {
          // Grant: insert into role_permissions
          await supabase?.from('role_permissions')?.insert({
            role,
            permission_id: perm?.id
          });
        } else if (!shouldHave && existing) {
          // Revoke: delete from role_permissions
          await supabase?.from('role_permissions')?.delete()?.eq('id', existing?.id);
        }
      }

      await logActivity('permission_change', 'role_permissions', null, { changes: pendingChanges });

      // Reload permissions to reflect saved state
      const roles = ['admin', 'operator', 'viewer'];
      const rolePermsData = {};
      for (const r of roles) {
        const perms = await getRolePermissions(r);
        rolePermsData[r] = perms;
      }
      setRolePermissions(rolePermsData);
      setPendingChanges({});
      setSaveSuccess('Permissions saved successfully.');
      setTimeout(() => setSaveSuccess(''), 4000);
    } catch (err) {
      console.error('Save permissions error:', err);
      setSaveError('Failed to save permissions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const groupedPermissions = permissions?.reduce((acc, perm) => {
    if (!acc?.[perm?.resource]) {
      acc[perm?.resource] = [];
    }
    acc?.[perm?.resource]?.push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Role Selector */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Select Role</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {roles?.map((role) => (
            <button
              key={role?.value}
              onClick={() => setSelectedRole(role?.value)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                selectedRole === role?.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon name={role?.icon} size={20} className={selectedRole === role?.value ? 'text-primary' : 'text-muted-foreground'} />
                <h4 className="font-semibold text-foreground">{role?.label}</h4>
              </div>
              <p className="text-sm text-muted-foreground">{role?.description}</p>
            </button>
          ))}
        </div>
      </div>
      {/* Permissions Matrix */}
      <div className="bg-card rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              Permissions for {roles?.find(r => r?.value === selectedRole)?.label}
            </h3>
            {Object.keys(pendingChanges)?.length > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">{Object.keys(pendingChanges)?.length} unsaved change(s)</span>
                <button
                  onClick={() => setPendingChanges({})}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Discard
                </button>
              </div>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Resource</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">View</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">Edit</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">Admin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {resources?.map((resource) => (
                  <tr key={resource?.key} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Icon name={resource?.icon} size={18} className="text-primary" />
                        <span className="text-sm font-medium text-foreground">{resource?.label}</span>
                      </div>
                    </td>
                    {actions?.map((action) => (
                      <td key={action} className="px-4 py-4 text-center">
                        <button
                          onClick={() => handleTogglePermission(selectedRole, resource?.key, action)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors hover:opacity-80"
                          title={`${hasPermission(selectedRole, resource?.key, action) ? 'Revoke' : 'Grant'} ${action} on ${resource?.label}`}
                        >
                          {hasPermission(selectedRole, resource?.key, action) ? (
                            <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center">
                              <Icon name="Check" size={16} className="text-success" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                              <Icon name="X" size={16} className="text-muted-foreground" />
                            </div>
                          )}
                        </button>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Save feedback */}
          {saveError && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
              <Icon name="AlertCircle" size={16} />
              <span>{saveError}</span>
            </div>
          )}
          {saveSuccess && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-sm text-success">
              <Icon name="CheckCircle" size={16} />
              <span>{saveSuccess}</span>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSavePermissions}
              disabled={saving || Object.keys(pendingChanges)?.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>Saving...</span></>
              ) : (
                <><Icon name="Save" size={16} /><span>Save Permissions</span></>
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Detailed Permissions List */}
      <div className="bg-card rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Detailed Permissions</h3>
          
          <div className="space-y-6">
            {Object.entries(groupedPermissions || {})?.map(([resource, perms]) => (
              <div key={resource}>
                <h4 className="text-md font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Folder" size={18} className="text-primary" />
                  {resource?.charAt(0)?.toUpperCase() + resource?.slice(1)}
                </h4>
                <div className="space-y-2 ml-6">
                  {perms?.map((perm) => (
                    <div
                      key={perm?.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-border"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{perm?.name}</p>
                        <p className="text-xs text-muted-foreground">{perm?.description}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {rolePermissions?.[selectedRole]?.some(rp => rp?.permissionId === perm?.id) ? (
                          <div className="flex items-center gap-1 text-success">
                            <Icon name="Check" size={18} />
                            <span className="text-xs font-medium">Granted</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Icon name="X" size={18} />
                            <span className="text-xs font-medium">Denied</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsTab;